import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { App as AntApp, Button, Dropdown, Input } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';
import { AsyncButton, Chip, Skeletons, RM, dt, LISTING_TYPE_TONE, LISTING_STATUS_TONE } from '../components/kit';
import AgentCard from '../components/AgentCard';
import * as listingsApi from '../api/listings';
import * as coShareApi from '../api/coShare';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import { checkUpload } from '../lib/uploadLimits';
import { compressImage } from '../lib/compressImage';
import { hasProjectMaterials } from '../lib/projectMaterialsLink';

const shortRM = (n) => (n >= 1000000 ? `RM ${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 2)}M` : `RM ${Number(n).toLocaleString('en-US')}`);
const UNIT_TONE = { AVAILABLE: 'ok', RESERVED: 'warn', SOLD: 'bad' };

// Enum columns arrive as FREEHOLD / PARTIALLY_FURNISHED. Printed raw they shout,
// and the long ones wrap their tile onto two lines.
const human = (v) => (v ? String(v).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()) : '');

const HERO_H = 244;
// Distance over which the hero hands the top of the screen to the title bar.
// Shorter than the hero itself, so the bar is solid before the photo is gone and
// there is never a moment where neither is carrying the page.
const HANDOFF = 168;

const reduceMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

// Mirrors VISIBILITY_LOCKED_STATUSES in listing.service.js — a draft is private,
// so it has no visibility to set until it is published. The server rejects it
// either way; this only keeps the control from offering a tap that always fails.
const VISIBILITY_LOCKED = ['DRAFT'];

// A co-share request that is still live — the agent has one running on this
// listing and the screen shows where it stands rather than offering to send
// another. Mirrors OPEN_STATUSES in coShare.service.js.
const CO_SHARE_OPEN = ['PENDING', 'ACCEPTED'];
const CO_SHARE_TONE = { PENDING: 'warn', ACCEPTED: 'ok', REJECTED: 'bad', WITHDRAWN: 'muted', CONVERTED: 'brand' };
// What each state means to the agent who ASKED. The lister's side of the same
// request is spelled out on the co-share screen itself.
const CO_SHARE_BLURB = {
  PENDING: 'Waiting on the listing agent. You’ll get a notification either way.',
  ACCEPTED: 'Accepted. The listing agent raises the sale from here — you go on it as a co-agent.',
  REJECTED: 'The listing agent declined this one.',
  WITHDRAWN: 'This request was pulled. You can send a new one.',
  CONVERTED: 'A sale was raised from this co-share.',
};
// The requester's default ask. Matches DEFAULT_SPLIT in coShare.service.js, so the
// figure the screen suggests is the figure the server would have assumed anyway.
const DEFAULT_SPLIT = '50';

export default function ListingDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { message, modal } = AntApp.useApp();
  const [listing, setListing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [slide, setSlide] = useState(0);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const fileRef = useRef(null);

  // Scroll-driven chrome. Every value below is written straight to the DOM inside a
  // rAF rather than held in state: this fires on every scroll frame, and a setState
  // here would re-render the whole record sixty times a second.
  const scrollRef = useRef(null);
  const heroBoxRef = useRef(null);   // clips — fades out as the hero leaves
  const heroLayerRef = useRef(null); // travels slower than the page (parallax)
  const barBgRef = useRef(null);     // solid bar, faded in behind the buttons
  const barTitleRef = useRef(null);
  const btnRefs = useRef([]);
  const ticking = useRef(false);

  const paintScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const y = el.scrollTop;
    const t = clamp01(y / HANDOFF);
    const soft = !reduceMotion();

    // Photo drifts at ~0.42x and dims, so the summary card reads as sliding over it.
    if (heroLayerRef.current) heroLayerRef.current.style.transform = soft ? `translate3d(0,${y * 0.42}px,0)` : 'none';
    if (heroBoxRef.current) heroBoxRef.current.style.opacity = soft ? String(1 - t * 0.72) : '1';

    // The bar itself is always there holding the buttons — only its surface and
    // title fade up, which is what makes the handoff feel like one movement.
    if (barBgRef.current) barBgRef.current.style.opacity = String(t);
    if (barTitleRef.current) {
      const tt = clamp01((t - 0.55) / 0.45);
      barTitleRef.current.style.opacity = String(tt);
      barTitleRef.current.style.transform = soft ? `translateY(${(1 - tt) * 7}px)` : 'none';
    }
    // Over a photo the round buttons need their own plate; once the bar is solid
    // that plate is just clutter, so it dissolves as the bar arrives.
    btnRefs.current.forEach((b) => {
      if (!b) return;
      b.style.background = `color-mix(in srgb, var(--surface) ${Math.round((1 - t) * 100)}%, transparent)`;
      b.style.boxShadow = `0 2px 8px rgba(8,14,24,${(0.20 * (1 - t)).toFixed(3)})`;
    });
  };

  const onScroll = () => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => { ticking.current = false; paintScroll(); });
  };

  // The record arrives after the first paint, so set the chrome once it does —
  // otherwise a restored scroll position renders against t = 0.
  useEffect(() => { paintScroll(); /* eslint-disable-next-line */ }, [listing]);

  const load = () =>
    listingsApi
      .getListingsById(id)
      .then((r) => setListing(r?.data ?? r))
      .catch((e) => message.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Another agent's listing (co-broke browse) → server flags read_only; every
  // mutating control below is hidden and a "listed by" contact card shows instead.
  const ro = !!listing?.read_only;
  // HQ-keyed stock is read-only too, but it is nobody's listing to co-share: there
  // is no agent on the other end to answer a request. It keeps the old behaviour
  // (an agent may write a booking form against house inventory), which is why every
  // co-share branch below tests this and not just `ro`.
  const isHqStock = !!listing?.is_hq_listing;
  // { can_request, blocked_reason, request } on someone else's listing;
  // { pending_count, accepted_count } on your own. See coShare.service.listingBlock.
  const coShare = listing?.co_share || null;
  const myRequest = coShare?.request || null;
  const openRequest = myRequest && CO_SHARE_OPEN.includes(myRequest.status) ? myRequest : null;
  // Who holds this listing. Falls back to the legacy agent_name field so an older
  // server response still renders a name rather than an empty card.
  const listingAgent = listing?.listing_agent
    || { name: listing?.agent_name || 'Another agent', phone: null, email: null, photo_url: null, ren_no: null, is_hq: !!listing?.is_hq_listing };

  const images = listing?.images || [];

  // Whether a PROJECT's individual units offer their own "Sale ›" shortcut in the
  // unit list. (The transactable-status test for the footer button lives in
  // primaryAction, which decides the whole bar in one place.)
  const canCreateProjectSale = listing && listing.listing_type === 'PROJECT' && !!listing.project_id && ['ACTIVE', 'UNDER_OFFER'].includes(listing.status);
  const createProjectSale = (unitId) =>
    nav('/sales/new', {
      state: {
        fromProject: {
          project_id: listing.project_id,
          ...(unitId ? { unit_id: unitId } : {}),
          property_address: listing.property_address || null,
        },
      },
    });
  /**
   * Ask the agent holding this listing to work the deal together.
   *
   * This is what replaced "Generate booking form" on another agent's property. A
   * form raised here would have taken their listing under offer without them ever
   * being asked — the servers refuse it now (assertListingIsOffereable), so the
   * screen offers the conversation instead of an action that would bounce.
   */
  const sendCoShareRequest = async ({ message: note, split }) => {
    const r = await coShareApi.createCoShareRequest(id, {
      message: note || null,
      proposed_split_percentage: split === '' || split === null ? null : Number(split),
    });
    setAskOpen(false);
    message.success('Co-share request sent');
    await load();
    const created = r?.data ?? r;
    if (created?.id) nav(`/co-share/${created.id}`);
  };

  const generateBookingForm = () =>
    nav('/forms/new', {
      state: {
        fromListing: {
          id: listing.id,
          listing_ref: listing.listing_ref,
          listing_type: listing.listing_type,
          property_title: listing.property_title,
          property_address: listing.property_address,
          asking_price_rm: listing.asking_price_rm,
          owner_name: listing.owner_name,
          owner_phone: listing.owner_phone,
          area: listing.area,
          state: listing.state,
          property_type: listing.property_type,
          is_mine: !ro,
          agent_name: listing.agent_name || null,
        },
        // The ALS/ALR this listing was raised from, when there is one. The agreement
        // inherits its verified owner details and agreed terms — those were settled
        // on a signed document, so they outrank the listing's own fields. Null on a
        // co-broke listing (the server withholds another agent's letter).
        fromAuthForm: listing.authorization_form || null,
      },
    });

  const setCover = async (imageId) => {
    await listingsApi.updateListingsImagesCoverByIdImageId(id, imageId);
    message.success('Cover image updated');
    load();
  };
  /**
   * The three actions that move a listing between the agent and HQ each hand
   * control somewhere it cannot be taken back from with one tap, so each one asks
   * first. Resolves once the work is done (or immediately on cancel) so the
   * AsyncButton keeps its spinner for the whole round trip rather than only until
   * the dialog opens.
   */
  const confirmThen = (opts, run) =>
    new Promise((resolve) => {
      modal.confirm({
        centered: true,
        okText: opts.okText,
        cancelText: 'Cancel',
        okButtonProps: opts.danger ? { danger: true } : undefined,
        title: opts.title,
        content: opts.content,
        onCancel: () => resolve(),
        onOk: () => run().then(resolve, (e) => { message.error(e.message); resolve(); }),
      });
    });

  // Publishing is the agent's own call — nothing is submitted to anyone. It stays
  // editable afterwards, and withdraw takes it back off the market.
  const publish = () =>
    confirmThen(
      {
        title: 'Publish this listing?',
        content: 'It goes live and you can start transacting on it. It stays private to you until you also set it public — that is what opens it to other agents for co-broke.',
        okText: 'Publish',
      },
      async () => {
        await listingsApi.updateListingsStatusById(id, { status: 'ACTIVE' });
        message.success('Listing published');
        await load();
      }
    );

  const setVisibility = async (next) => {
    await listingsApi.updateListingsVisibilityById(id, { visibility: next });
    message.success(next === 'PUBLIC' ? 'Listing is now public' : 'Listing is now internal only');
    await load();
  };
  const changeStatus = async (status, ok) => {
    await listingsApi.updateListingsStatusById(id, { status });
    message.success(ok);
    await load();
  };

  // Withdraw / expire / reactivate are reversible, but they are one-tap menu items
  // with no spinner of their own, so each states what it does first. Same set of
  // confirmations the admin portal puts on the same three transitions.
  const withdraw = () => confirmThen(
    {
      title: 'Withdraw this listing?',
      content: 'It comes off the market and stops being selectable for new sales. You can reactivate it later.',
      okText: 'Withdraw',
      danger: true,
    },
    () => changeStatus('WITHDRAWN', 'Listing withdrawn')
  );

  const markExpired = () => confirmThen(
    {
      title: 'Mark this listing as expired?',
      content: 'It stops being selectable for new sales. You can reactivate it later.',
      okText: 'Mark expired',
    },
    () => changeStatus('EXPIRED', 'Listing marked expired')
  );

  const reactivate = () => confirmThen(
    {
      title: 'Reactivate this listing?',
      content: 'It goes back to active and can be sold again.',
      okText: 'Reactivate',
    },
    () => changeStatus('ACTIVE', 'Listing reactivated')
  );
  // Sharing is parked: the toolbar button is disabled until the public listing site
  // exists. The server route and `listingsApi.createListingsShareById` are both
  // still there — switching it on is an onClick, not a rebuild.
  //
  // Listing → sale conversion used to live here too. It was dropped for parity with
  // the admin portal, which has no such action: both apps now raise a sale from the
  // sale form and link the listing there. POST /listings/:id/convert still exists
  // server-side but nothing calls it.
  const discardDraft = () =>
    confirmThen(
      {
        title: 'Discard this draft?',
        content: `${listing?.listing_ref || 'This listing'} and its photos are deleted for good. There is no undo.`,
        okText: 'Discard draft',
        danger: true,
      },
      async () => {
        await listingsApi.deleteListingsById(id);
        message.success('Draft listing discarded');
        nav('/listings');
      }
    );

  /**
   * The one thing an agent is most likely on this screen to do, given where the
   * listing sits. It goes in the sticky footer.
   *
   * Previously only a live listing had a footer action, so on a draft the whole
   * point of the screen — publish it — was a half-width button six sections down,
   * carrying the same weight as "Discard draft" next to it. A primary action you
   * have to scroll to is not a primary action.
   */
  const primaryAction = (() => {
    if (!listing) return null;
    const s = listing.status;
    // A project sells through its units, not through an offer on the listing, so
    // the listing being under offer does not block it. Works on HQ project stock
    // as well as your own — projects are firm inventory with no agent to displace.
    if (['ACTIVE', 'UNDER_OFFER'].includes(s) && listing.listing_type === 'PROJECT' && listing.project_id) {
      return { label: 'Create sale · select unit', run: () => createProjectSale(null) };
    }

    // ── Another AGENT's listing ────────────────────────────────────────────────
    // No paperwork here at all: a booking form on it would put their property
    // under offer without asking them, and both servers now refuse it. The one
    // move is the co-share request, and once one is running the button carries you
    // to it rather than sending a second.
    if (ro && !isHqStock) {
      if (openRequest) {
        return {
          label: openRequest.status === 'ACCEPTED' ? 'Co-share accepted — open' : 'Co-share requested — view',
          run: () => nav(`/co-share/${openRequest.id}`),
        };
      }
      if (coShare?.can_request) return { label: 'Request co-share', run: () => setAskOpen(true) };
      return null;
    }

    // Your own listing, or HQ house stock. ACTIVE only for a booking form: a
    // listing already under offer cannot take another one. The server rejects it,
    // and approving a second form was never possible anyway (approveForm locks on
    // the listing still being ACTIVE), so offering it only produced forms that were
    // dead on arrival.
    if (s === 'ACTIVE' && ['SUBSALE', 'RENT'].includes(listing.listing_type)) {
      return { label: 'Generate booking form', run: generateBookingForm };
    }
    if (ro) return null; // everything past here mutates someone else's listing
    if (s === 'DRAFT') return { label: 'Publish listing', run: publish };
    if (['WITHDRAWN', 'EXPIRED'].includes(s)) return { label: 'Reactivate listing', run: reactivate };
    return null;
  })();

  /**
   * Everything else that mutates the listing, as overflow-menu items. Rare and
   * deliberate actions (withdraw, expire, discard) do not belong in the scroll
   * competing with the primary one; each already confirms before it runs.
   * `danger` is reserved for the irreversible one.
   */
  const menuItems = !listing ? [] : [
    { key: 'edit', label: 'Edit listing' },
    { key: 'photos', label: `Manage photos (${images.length})` },
    // No "Convert → draft sale" here, deliberately: the admin portal has no such
    // action either, and a sale is raised from the other end in both apps —
    // Sales → New, then link the listing (SaleCreatePage's ListingLinkField,
    // status="ACTIVE", mirroring the portal's ListingSelect). Same capability,
    // one direction, so the two apps agree.
    ...(listing.status === 'ACTIVE'
      ? [{ type: 'divider' }, { key: 'withdraw', label: 'Withdraw listing' }, { key: 'expire', label: 'Mark as expired' }]
      : []),
    ...(listing.status === 'DRAFT'
      ? [{ type: 'divider' }, { key: 'discard', label: 'Discard draft', danger: true }]
      : []),
  ];

  const onMenuClick = ({ key }) => {
    if (key === 'edit') nav(`/listings/${id}/edit`);
    else if (key === 'photos') setPhotosOpen(true);
    else if (key === 'withdraw') withdraw();
    else if (key === 'expire') markExpired();
    else if (key === 'discard') discardDraft();
  };

  // Signed-URL photo upload: upload-url → PUT bytes straight to Supabase (bare axios,
  // no app interceptors) → confirm so the server persists the row → reload.
  const handleFile = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    setUploading(true);
    try {
      // Shrink a camera-sized photo before it goes anywhere, then check what came
      // out: Storage rejects an oversized or wrong-format object with a bare 400,
      // so say which file is wrong and why rather than passing that on.
      const file = await compressImage(picked);
      const problem = checkUpload(file);
      if (problem) { message.warning(problem); return; }

      const ct = file.type || 'application/octet-stream';
      const up = (await listingsApi.createListingsImagesUploadUrlById(id, { fileName: file.name, fileSize: file.size, contentType: ct }))?.data;
      if (!up?.uploadUrl) throw new Error('Could not get an upload URL');
      try {
        await axios.put(up.uploadUrl, file, {
          headers: { 'Content-Type': ct, 'x-upsert': 'false' },
          onUploadProgress: (evt) => { if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100)); },
        });
      } catch {
        throw new Error(`Could not upload “${file.name}”. Please try again.`);
      }
      await listingsApi.createListingsImagesConfirmUploadById(id, { storagePath: up.path });
      message.success('Photo added');
      await load();
    } catch (err) {
      message.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  // Confirmed: the photo is deleted from storage, not just unpinned from the listing,
  // and on a phone the Remove button sits a thumb's width from "Set as cover".
  const removeImage = (imageId) => {
    modal.confirm({
      title: 'Remove this photo?',
      content: 'It is deleted from the listing and from storage. This cannot be undone — you would have to upload it again.',
      okText: 'Remove photo',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      centered: true,
      onOk: async () => {
        try {
          await listingsApi.deleteListingsImagesByIdImageId(id, imageId);
        } catch (e) {
          // Surface it, then rethrow so the dialog stays open on a server refusal.
          message.error(e.message);
          throw e;
        }
        message.success('Photo removed');
        await load();
      },
    });
  };

  // Which slide the gallery has settled on. Read off scrollLeft rather than tracked
  // per-swipe so a fling that crosses two slides still reports the right number.
  const onGalleryScroll = (e) => {
    const el = e.currentTarget;
    const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
    setSlide((prev) => (prev === i ? prev : i));
  };

  const priceLabel = listing && (listing.listing_type === 'PROJECT'
    ? (listing.project_stats?.price_min != null
      ? `${shortRM(listing.project_stats.price_min)} – ${shortRM(listing.project_stats.price_max)}`
      : 'View units')
    : RM(listing.asking_price_rm));
  // A rental asking price is a monthly figure. Without the suffix RM 2,800 next to
  // RM 920,000 on the previous screen reads as an absurdly cheap property.
  const priceSuffix = listing?.listing_type === 'RENT' ? '/month' : null;

  // Price psf — the figure agents compare on, and the one the API never sends
  // because it is pure arithmetic. Meaningless for rentals and unpriced projects.
  const psf = (() => {
    if (!listing || ['RENT', 'PROJECT'].includes(listing.listing_type)) return null;
    const area = Number(listing.built_up_sqft) || Number(listing.land_area_sqft) || 0;
    const price = Number(listing.asking_price_rm) || 0;
    if (!area || !price) return null;
    return `RM ${Math.round(price / area).toLocaleString('en-US')} psf`;
  })();

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* ── title bar ───────────────────────────────────────────────────────────
          Always present so back and the actions menu never move, but transparent
          over the photo and solid once the hero has scrolled away. The container
          ignores taps so the hero underneath stays swipeable; only the controls
          and the solid surface take them. */}
      {listing && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20, pointerEvents: 'none' }}>
          <div
            ref={barBgRef}
            style={{ position: 'absolute', inset: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', opacity: 0, pointerEvents: 'auto' }}
          />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 15px' }}>
            <RoundButton innerRef={(el) => { btnRefs.current[0] = el; }} label="Go back" onClick={() => nav(-1)}>
              <path d="m15 6-6 6 6 6" />
            </RoundButton>

            <div ref={barTitleRef} style={{ flex: 1, minWidth: 0, opacity: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {listing.property_title}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace' }}>{listing.listing_ref}</div>
            </div>

            {/* Public link — parked until the public listing site is live, so it is
                shown disabled rather than hidden: the capability is coming and the
                slot should not move when it arrives. */}
            <RoundButton
              innerRef={(el) => { btnRefs.current[1] = el; }}
              label="Share public link (not available yet)"
              disabled
              title="Public listing links are not switched on yet"
            >
              <><path d="M4 12v7h16v-7" /><path d="M12 3v13M8 7l4-4 4 4" /></>
            </RoundButton>

            {!ro && (
              <Dropdown
                trigger={['click']}
                menu={{ items: menuItems, onClick: onMenuClick }}
              >
                <div style={{ pointerEvents: 'auto', display: 'flex' }}>
                  <RoundButton innerRef={(el) => { btnRefs.current[2] = el; }} label="Listing actions">
                    <path d="M4 7h16M4 12h16M4 17h16" />
                  </RoundButton>
                </div>
              </Dropdown>
            )}
          </div>
        </div>
      )}

      {!listing ? (
        <div style={{ padding: 18 }}><Skeletons n={3} height={90} /></div>
      ) : (
        <div ref={scrollRef} onScroll={onScroll} className="scrx" style={{ flex: 1, overflowY: 'auto' }}>
          {/* ── hero ────────────────────────────────────────────────────────────
              Full-bleed swipe gallery. The page opens on the property rather than
              on a toolbar, so it is the one screen you can turn towards a client.
              Nothing but the photo counter sits on the photo now — type, status
              and visibility all read from the summary card below, where they have
              a background to sit on and never fight the image.

              The outer box clips; the inner layer is what moves. Translating the
              box itself would let the photo hang below the summary card and show
              through the gaps between the sections underneath. */}
          <div
            ref={heroBoxRef}
            style={{ position: 'relative', height: HERO_H, overflow: 'hidden', background: 'repeating-linear-gradient(45deg,var(--surface2),var(--surface2) 7px,var(--chip) 7px,var(--chip) 14px)' }}
          >
            <div ref={heroLayerRef} style={{ position: 'absolute', inset: 0, willChange: 'transform' }}>
              {images.length > 0 && (
                <div
                  className="scrx"
                  onScroll={onGalleryScroll}
                  style={{ position: 'absolute', inset: 0, display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory' }}
                >
                  {images.map((im) => (
                    <div key={im.id} style={{ flex: 'none', width: '100%', height: '100%', scrollSnapAlign: 'center' }}>
                      {im.image_url && <img src={im.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Two scrims, both doing a job: the top one darkens whatever is behind
                the round controls, the bottom one gives the white pager dots
                something to sit on and settles the edge the summary card overlaps. */}
            <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 96, background: 'linear-gradient(rgba(8,14,24,.38),rgba(8,14,24,0))', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 110, background: 'linear-gradient(to top,rgba(8,14,24,.45),rgba(8,14,24,0))', pointerEvents: 'none' }} />

            {images.length > 1 && (
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 56, display: 'flex', justifyContent: 'center', gap: 5, pointerEvents: 'none' }}>
                {images.map((im, i) => (
                  <span
                    key={im.id}
                    style={{ width: i === slide ? 15 : 5, height: 5, borderRadius: 999, background: i === slide ? '#fff' : 'rgba(255,255,255,.55)', boxShadow: '0 0 3px rgba(8,14,24,.45)', transition: 'width .22s var(--sheet-ease)' }}
                  />
                ))}
              </div>
            )}
            {/* On someone else's listing with no photos there is nothing to count
                and nothing to add, so the pill is not rendered at all rather than
                offering an action that would do nothing. */}
            {(images.length > 0 || !ro) && (
              <div
                onClick={() => !ro && setPhotosOpen(true)}
                style={{ position: 'absolute', right: 12, bottom: 52, fontSize: 11.5, fontWeight: 600, color: '#fff', background: 'rgba(10,16,26,.55)', borderRadius: 999, padding: '3px 10px', cursor: ro ? 'default' : 'pointer' }}
              >
                {images.length > 0 ? `${Math.min(slide + 1, images.length)} / ${images.length}` : '＋ Add photos'}
              </div>
            )}
          </div>

          {/* ── summary, overlapping the hero ───────────────────────────────── */}
          <div className="rise-in" style={{ margin: '-44px 16px 0', position: 'relative', zIndex: 3, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 16, boxShadow: '0 10px 28px rgba(12,20,34,.14)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
              <Chip label={listing.listing_type} tone={LISTING_TYPE_TONE[listing.listing_type]} />
              <Chip label={String(listing.status || '').replace(/_/g, ' ')} tone={LISTING_STATUS_TONE[listing.status]} />
              <span style={{ flex: 1 }} />
              {ro
                ? <Chip label={listing.visibility} tone={listing.visibility === 'PUBLIC' ? 'ok' : 'brand'} />
                : (
                  // Wrapped, not tagged directly: VisibilityToggle has a closed
                  // prop signature and would drop data-tour. inline-flex (not
                  // display:contents) so the wrapper has a real box for the
                  // tour spotlight to measure.
                  <span data-tour="listing-visibility" style={{ display: 'inline-flex' }}>
                    <VisibilityToggle
                      value={listing.visibility}
                      locked={VISIBILITY_LOCKED.includes(listing.status)}
                      onChange={setVisibility}
                      onError={(e) => message.error(e.message)}
                    />
                  </span>
                )}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, letterSpacing: '-.012em' }}>{listing.property_title}</div>
            {/* On someone else's listing the server sends no street address, only
                the locality — so this renders "Cheras, Kuala Lumpur, 56000" with
                the door number withheld. Say so rather than just dropping the
                line: an agent should know the address exists and who has it. */}
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4, lineHeight: 1.45 }}>
              {listing.property_address && <>{listing.property_address}<br /></>}
              {[listing.area, listing.state, listing.postcode].filter(Boolean).join(', ')}
              {ro && !listing.property_address && (
                <><br /><span style={{ fontStyle: 'italic', opacity: 0.75 }}>Exact address from the listing agent</span></>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 11, paddingTop: 11, borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-.028em', lineHeight: 1 }}>
                {priceLabel}
                {priceSuffix && <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted)' }}>{priceSuffix}</span>}
              </span>
              {psf && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{psf}</span>}
              <span style={{ flex: 1 }} />
              {listing.price_negotiable !== false && listing.listing_type !== 'PROJECT' && (
                <Chip label="NEGOTIABLE" tone="ok" style={{ marginBottom: 2 }} />
              )}
            </div>
          </div>

          <div className="rise-in d1" style={{ padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* co-broke — whose listing this is and how to reach them. The property
                owner's details are deliberately absent (the server redacts them):
                a co-broke starts with a call to the listing agent, not the vendor. */}
            {ro && (
              <AgentCard
                agent={listingAgent}
                note={
                  listingAgent.is_hq
                    ? 'CID HQ stock — ask your branch admin to arrange a viewing.'
                    : 'Reach out, then send a co-share request so the split is on record.'
                }
                waText={`Hi ${listingAgent.name}, I have a buyer for ${listing.listing_ref} (${listing.property_title}). Are you open to co-broke?`}
              />
            )}

            {/* Where your co-share stands on someone else's listing. Shown for a
                closed request too (rejected / withdrawn / converted): without it the
                page would just offer "Request co-share" again as though nothing had
                ever happened. */}
            {ro && !isHqStock && myRequest && (
              <div
                onClick={() => nav(`/co-share/${myRequest.id}`)}
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 7, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)', flex: 1 }}>YOUR CO-SHARE REQUEST</span>
                  <Chip label={myRequest.status} tone={CO_SHARE_TONE[myRequest.status]} />
                  <span style={{ color: 'var(--muted)' }}>›</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                  {CO_SHARE_BLURB[myRequest.status] || ''}
                  {myRequest.status === 'ACCEPTED' && myRequest.agreed_split_percentage != null
                    && ` Agreed split ${myRequest.agreed_split_percentage}% to you.`}
                </div>
                {myRequest.response_note && (
                  <div style={{ fontSize: 12.5, lineHeight: 1.5, fontStyle: 'italic' }}>“{myRequest.response_note}”</div>
                )}
              </div>
            )}

            {/* Why there is no booking-form button on this screen. Said once, plainly
                — otherwise the absence of the action an agent used to have here just
                reads as something being broken. */}
            {ro && !isHqStock && !myRequest && (
              <div style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, letterSpacing: '.07em', color: 'var(--muted)' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3" /><path d="M3 19a6 6 0 0 1 12 0" /><path d="M17 8h4M19 6v4" /></svg>
                  CO-SHARE, NOT PAPERWORK
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                  {coShare?.blocked_reason
                    || 'This property belongs to another agent, so you cannot raise a booking form on it. Send a co-share request instead — once they accept, they raise the sale with you on it as a co-agent.'}
                </div>
              </div>
            )}

            {/* Your own listing: who is waiting on an answer from you. */}
            {!ro && coShare && (coShare.pending_count > 0 || coShare.accepted_count > 0) && (
              <div
                onClick={() => nav('/co-share')}
                style={{ background: 'color-mix(in srgb,var(--primary) 7%,var(--surface))', border: '1px solid color-mix(in srgb,var(--primary) 30%,var(--border))', borderRadius: 14, padding: '15px 16px', display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer' }}
              >
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--surface)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="9" r="3" /><circle cx="16" cy="9" r="3" /><path d="M3 19a5 5 0 0 1 10 0" /><path d="M13.5 15.5a5 5 0 0 1 7.5 3.5" /></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {coShare.pending_count > 0
                      ? `${coShare.pending_count} co-share request${coShare.pending_count > 1 ? 's' : ''} waiting`
                      : `${coShare.accepted_count} accepted co-share${coShare.accepted_count > 1 ? 's' : ''}`}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                    {coShare.pending_count > 0
                      ? 'Another agent has a buyer for this property.'
                      : 'Raise the sale — the co-agent goes on it automatically.'}
                  </div>
                </div>
                <span style={{ color: 'var(--muted)' }}>›</span>
              </div>
            )}

            {/* headline specs — a four-up strip reads faster than four tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              <Stat k="BEDS" v={listing.bedrooms ?? '—'} />
              <Stat k="BATHS" v={listing.bathrooms ?? '—'} />
              <Stat k="CARS" v={listing.car_parks ?? '—'} />
              <Stat k="SQFT" v={listing.built_up_sqft ? Number(listing.built_up_sqft).toLocaleString('en-US') : '—'} last />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Spec k="PROPERTY TYPE" v={listing.property_type || '—'} />
              <Spec k="LAND AREA" v={listing.land_area_sqft ? `${Number(listing.land_area_sqft).toLocaleString('en-US')} sqft` : '—'} />
              <Spec k="TENURE / FURNISH" v={[human(listing.property_tenure), human(listing.furnish)].filter(Boolean).join(' · ') || '—'} />
              <Spec k="FACING / FLOOR" v={[listing.facing, listing.floor_level].filter(Boolean).join(' · ') || '—'} />
            </div>

            {listing.description && (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px' }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 6 }}>DESCRIPTION</div>
                <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{listing.description}</div>
              </div>
            )}

            {Object.keys(listing.features || {}).filter((k) => listing.features[k]).length > 0 && (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 6 }}>FEATURES</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.keys(listing.features).filter((k) => listing.features[k]).map((k) => (
                    <Chip key={k} label={k.replace(/_/g, ' ')} />
                  ))}
                </div>
              </div>
            )}

            {/* The project's brochures, price lists, photos and videos from HQ. */}
            {hasProjectMaterials(listing) && (
              <div role="button" tabIndex={0} onClick={() => nav(`/projects/${listing.project_id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') nav(`/projects/${listing.project_id}`); }}
                className="tap"
                style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '12px 14px', cursor: 'pointer' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--chip)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20V9l8-5 8 5v11" /><path d="M9 20v-6h6v6" /><path d="M3 20h18" /></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Project materials</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>brochures · price lists · photos · videos</div>
                </div>
                <span style={{ color: 'var(--muted)' }}>›</span>
              </div>
            )}

            {/* PROJECT units — stats + full unit list (mirrors the admin project view) */}
            {listing.listing_type === 'PROJECT' && listing.project_stats && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <Spec k="AVAILABLE" v={listing.project_stats.available_units} />
                  <Spec k="RESERVED" v={listing.project_stats.reserved_units} />
                  <Spec k="SOLD" v={`${listing.project_stats.sold_units} / ${listing.project_stats.total_units}`} />
                </div>
                <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', color: 'var(--muted)' }}>PROJECT UNITS</div>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                  {(listing.project_units || []).map((u, i, arr) => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 13px', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {u.unit_no}{u.unit_type ? ` · ${u.unit_type}` : ''}{u.bumi_lot ? ' · Bumi' : ''}
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1 }}>
                          {[u.block && `Block ${u.block}`, u.floor && `L${u.floor}`, u.built_up_sqft && `${Number(u.built_up_sqft)} sqft`, u.bedrooms != null && `${u.bedrooms}bd/${u.bathrooms ?? '—'}ba`].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flex: 'none' }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{u.net_price_rm != null ? RM(u.net_price_rm) : '—'}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {canCreateProjectSale && u.status === 'AVAILABLE' && (
                            <span onClick={() => createProjectSale(u.id)} style={{ cursor: 'pointer', fontSize: 11.5, fontWeight: 700, color: 'var(--primary)' }}>Sale ›</span>
                          )}
                          <Chip label={u.status} tone={UNIT_TONE[u.status]} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* owner + linked form + agents — your own listing only. On someone
                else's, every row here was the listing agent's name a second time
                ("Listed by TL2" / "Agents TL2 (lead)"), because the co-broke
                response carries exactly one agent: the person already on the card
                above. The vendor's rows never belonged on a co-broke view either. */}
            {!ro && (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '4px 14px' }}>
                <Row k="Owner" v={listing.owner_name || '—'} />
                <Row k="Owner phone" v={listing.owner_phone || '—'} mono />
                <Row k="Owner email" v={listing.owner_email || '—'} />
                <Row tour="listing-auth-row" k="Authorization form" v={listing.authorization_form ? `${listing.authorization_form.form_ref} (${listing.authorization_form.status})` : '—'} mono={!!listing.authorization_form} accent={!!listing.authorization_form} />
                <Row k="Agents" v={(listing.agents || []).map((a) => `${a.name}${a.is_primary ? ' (lead)' : ''}`).join(', ') || '—'} last={!listing.published_at} />
                {/* Only rendered once it has been published — a draft has no dates to show. */}
                {listing.published_at && (
                  <Row
                    k="Live since"
                    v={listing.expired_at ? `${dt(listing.published_at)} → ${dt(listing.expired_at)}` : dt(listing.published_at)}
                    last
                  />
                )}
              </div>
            )}

            {/* No action grid here any more. The primary action is the sticky
                footer and everything else is in the bar menu, so the scroll is
                purely the record. */}

            {/* What the draft/published split means — the only rule left on this
                screen now that nothing goes to HQ for approval. */}
            {!ro && (
              <div style={{ background: 'var(--surface)', border: '1.5px dashed var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 600, letterSpacing: '.07em', color: 'var(--muted)' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11 V8 a4 4 0 0 1 8 0 v3" /></svg>
                  WHO CAN SEE THIS
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                  {VISIBILITY_LOCKED.includes(listing.status)
                    ? 'A draft is yours alone — nobody else sees it. Publish it and you choose whether to share it.'
                    : listing.visibility === 'PUBLIC'
                      ? 'Public. Other agents can find this listing and approach you to co-broke it. Switch it back above at any time.'
                      : 'Internal. Live and yours to transact on, but no other agent can see it — set it public above to open it for co-broke.'}
                </div>
              </div>
            )}
            <div style={{ height: 6 }} />
          </div>
        </div>
      )}

      {/* Bottom action bar — whatever this listing's state makes the obvious next
          move. See primaryAction. AsyncButton rather than Button because two of
          the four (publish, reactivate) are round trips that confirm first. */}
      {primaryAction && (
        <div data-tour="listing-primary-action" style={{ flex: 'none', padding: '14px 16px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
          <AsyncButton
            type="primary"
            block
            size="large"
            onClick={primaryAction.run}
            onError={(e) => message.error(e.message)}
            style={{ height: 46, fontWeight: 600 }}
          >
            {primaryAction.label}
          </AsyncButton>
        </div>
      )}

      <CoShareAskSheet
        open={askOpen && ro && !isHqStock}
        listing={listing}
        agentName={listingAgent.name}
        onSubmit={sendCoShareRequest}
        onError={(e) => message.error(e.message)}
        onClose={() => setAskOpen(false)}
      />

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} style={{ display: 'none' }} />
      <PhotoSheet
        open={photosOpen && !ro}
        images={images}
        uploading={uploading}
        uploadPct={uploadPct}
        onAdd={() => fileRef.current?.click()}
        onSetCover={setCover}
        onRemove={removeImage}
        onError={(e) => message.error(e.message)}
        onClose={() => setPhotosOpen(false)}
      />
    </div>
  );
}

/**
 * The co-share ask — the one thing an agent can do on someone else's property.
 *
 * Two fields, both optional to the server but both worth asking for: what you have
 * (a buyer, a viewing, a deadline) and what you want out of it. The split is the
 * part that otherwise gets agreed over WhatsApp and remembered differently by each
 * side — putting a number on the request is what makes the sale's agent split
 * something both agents already saw.
 *
 * The percentage is YOUR share of the agent commission; the listing agent keeps the
 * rest and can correct the figure when they accept.
 */
function CoShareAskSheet({ open, listing, agentName, onSubmit, onError, onClose }) {
  const { mounted, closing } = useSheetAnim(open);
  const [note, setNote] = useState('');
  const [split, setSplit] = useState(DEFAULT_SPLIT);
  const [busy, setBusy] = useState(false);

  // A reopened sheet starts clean rather than holding the last attempt's text.
  useEffect(() => { if (open) { setNote(''); setSplit(DEFAULT_SPLIT); } }, [open]);

  // Every open starts from the same two values, so anything else is the agent's.
  const dirty = note.trim() !== '' || split !== DEFAULT_SPLIT;
  const guard = useUnsavedChanges(open && dirty, { what: 'your co-share request' });
  const close = () => guard.confirmClose(onClose);

  if (!mounted) return null;

  const splitN = split === '' ? null : Number(split);
  const badSplit = split !== '' && (!Number.isFinite(splitN) || splitN < 0 || splitN > 100);

  const send = async () => {
    if (badSplit || busy) return;
    setBusy(true);
    try {
      await onSubmit({ message: note.trim(), split });
    } catch (e) {
      onError?.(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={close} className={`sheet-scrim${closing ? ' is-closing' : ''}`} style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className={`sheet-panel${closing ? ' is-closing' : ''}`} style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '88%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Request co-share</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
            {listing?.listing_ref} · {agentName}
          </div>
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 6 }}>WHAT YOU HAVE</div>
            <Input.TextArea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              autoSize={{ minRows: 3, maxRows: 7 }}
              placeholder={`Hi ${agentName}, I have a buyer for this — budget and timeline…`}
            />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 5, lineHeight: 1.45 }}>
              They see this with the request. A real buyer, said plainly, is what gets answered.
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 6 }}>YOUR SHARE OF THE SPLIT</div>
            <Input
              value={split}
              onChange={(e) => setSplit(e.target.value.replace(/[^\d.]/g, ''))}
              inputMode="decimal"
              suffix="%"
              status={badSplit ? 'error' : undefined}
              style={{ height: 'var(--field-h)' }}
            />
            <div style={{ fontSize: 11.5, color: badSplit ? 'var(--bad)' : 'var(--muted)', marginTop: 5, lineHeight: 1.45 }}>
              {badSplit
                ? 'Enter a percentage between 0 and 100.'
                : `Your cut of the agent commission — ${agentName} keeps ${Number.isFinite(splitN) ? Number((100 - splitN).toFixed(2)) : '…'}%. They can adjust it when they accept.`}
            </div>
          </div>

          <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 15px', fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.55 }}>
            If they accept, <b style={{ color: 'var(--text)' }}>they</b> raise the sale and you are added to it as a co-agent at the agreed split. You do not write the booking form — the authorisation to sell is theirs.
          </div>
        </div>

        <div style={{ flex: 'none', padding: '14px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
          <Button block onClick={close} style={{ height: 44 }}>Cancel</Button>
          <Button type="primary" block loading={busy} disabled={badSplit} onClick={send} style={{ height: 44, fontWeight: 600 }}>
            Send request
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Photo management, lifted off the hero. Every slide used to carry its own
 * "Set cover" and "✕" button, which is three controls per photo sitting on top of
 * the thing they're meant to be showing. Here the hero stays a photo and the
 * controls live one tap away, where there is room to label them.
 */
function PhotoSheet({ open, images, uploading, uploadPct, onAdd, onSetCover, onRemove, onError, onClose }) {
  const { mounted, closing } = useSheetAnim(open);
  if (!mounted) return null;
  return (
    <div onClick={onClose} className={`sheet-scrim${closing ? ' is-closing' : ''}`} style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className={`sheet-panel${closing ? ' is-closing' : ''}`} style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '80%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px 10px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>Photos</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{images.length} added</div>
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {images.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', padding: '18px 0' }}>
              No photos yet. A listing with a photo is the one an agent can recognise.
            </div>
          )}
          {images.map((im) => (
            <div key={im.id} style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
              <div style={{ width: 86, height: 62, borderRadius: 10, overflow: 'hidden', flex: 'none', border: '1px solid var(--border)', background: 'var(--surface2)' }}>
                {im.image_url && <img src={im.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {im.is_cover
                  ? <Chip label="COVER" tone="brand" />
                  : (
                    <AsyncButton size="small" onClick={() => onSetCover(im.id)} onError={onError}>Set as cover</AsyncButton>
                  )}
              </div>
              <Button size="small" danger onClick={() => onRemove(im.id)}>Remove</Button>
            </div>
          ))}
        </div>

        <div style={{ flex: 'none', padding: '14px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
          <Button block onClick={onClose} style={{ height: 42 }}>Done</Button>
          <Button type="primary" block loading={uploading} onClick={onAdd} style={{ height: 42, fontWeight: 600 }}>
            {/* 0% means the resize is still running — the bytes haven't started moving. */}
            {uploading ? (uploadPct > 0 ? `Uploading ${uploadPct}%` : 'Preparing…') : '＋ Add photo'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Internal ⇄ Public, the agent's own call once the listing is published.
 *
 * A segmented control rather than a switch because neither side is an "off" state:
 * INTERNAL is a real, useful place for a listing to sit, not the absence of PUBLIC.
 * While the listing is a draft or sitting with HQ the control still shows — an
 * agent should be able to see where the listing will land — but it is inert and
 * says why.
 */
function VisibilityToggle({ value, locked, onChange, onError }) {
  const [busy, setBusy] = useState(null);
  const pick = async (next) => {
    if (locked || busy || next === value) return;
    setBusy(next);
    try {
      await onChange(next);
    } catch (e) {
      onError?.(e);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div
      title={locked ? 'A draft is private — publish it to choose internal or public' : undefined}
      style={{ display: 'flex', gap: 2, padding: 2, borderRadius: 999, background: 'var(--surface2)', border: '1px solid var(--border)', opacity: locked ? 0.55 : 1, cursor: locked ? 'not-allowed' : 'default' }}
    >
      {['INTERNAL', 'PUBLIC'].map((v) => {
        const on = value === v;
        return (
          <span
            key={v}
            role="button"
            tabIndex={locked ? -1 : 0}
            aria-pressed={on}
            aria-disabled={locked || undefined}
            onClick={() => pick(v)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(v); } }}
            style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '.04em', padding: '3px 9px', borderRadius: 999,
              background: on ? (v === 'PUBLIC' ? 'var(--ok)' : 'var(--primary)') : 'transparent',
              color: on ? '#fff' : 'var(--muted)',
              cursor: locked || on ? 'inherit' : 'pointer',
              opacity: busy && busy !== v ? 0.5 : 1,
              transition: 'background .18s ease, color .18s ease',
            }}
          >
            {busy === v ? '…' : v}
          </span>
        );
      })}
    </div>
  );
}

/**
 * The bar's controls. One button that works over a photograph and over the solid
 * bar, rather than two that cross-fade: the icon is always the app's text colour
 * and the plate behind it dissolves as the bar arrives (paintScroll writes the
 * background and shadow). Nothing here has to know about scrolling.
 */
const RoundButton = ({ children, onClick, label, innerRef, disabled, title }) => (
  <div
    ref={innerRef}
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-label={label}
    aria-disabled={disabled || undefined}
    title={title}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(e) => { if (!disabled && onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(e); } }}
    style={{ width: 34, height: 34, borderRadius: 999, background: 'var(--surface)', boxShadow: '0 2px 8px rgba(8,14,24,.2)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer', flex: 'none', pointerEvents: 'auto', opacity: disabled ? 0.45 : 1 }}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
  </div>
);

const Stat = ({ k, v, last }) => (
  <div style={{ textAlign: 'center', padding: '11px 4px', borderRight: last ? 'none' : '1px solid var(--border)' }}>
    <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-.01em', fontVariantNumeric: 'tabular-nums' }}>{v}</div>
    <div style={{ fontSize: 10.5, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.06em', marginTop: 2 }}>{k}</div>
  </div>
);

const Spec = ({ k, v, wide }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 15px', gridColumn: wide ? '1 / -1' : 'auto' }}>
    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.05em' }}>{k}</div>
    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{v}</div>
  </div>
);

const Row = ({ k, v, mono, accent, last, tour }) => (
  <div data-tour={tour} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '10px 0', borderBottom: last ? 'none' : '1px solid var(--border)', fontSize: 13 }}>
    <span style={{ color: 'var(--muted)', flex: 'none' }}>{k}</span>
    <span style={{ fontWeight: 600, textAlign: 'right', fontFamily: mono ? 'ui-monospace,monospace' : 'inherit', color: accent ? 'var(--primary)' : 'var(--text)' }}>{v}</span>
  </div>
);
